import { useEffect, useCallback } from 'react';
import {
  Breadcrumbs,
  Link,
  Page,
  PageBody,
  PageBreadcrumbs,
  PageHeader,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Banner,
  Stack,
  Spinner,
  Text,
  Box,
  InlineStack,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import { AuditLogsTable } from './audit-logs-table';
import { useExportAuditLogsModal } from './export-audit-logs-modal';
import type { AuditLogResponse } from '../types';
import { useCustomerFeedback } from '@/features/customer-feedback';
import { useAuditLogsData } from '../hooks/use-audit-logs-data';
import { PageTitle } from '@/components/page-title';
import type { BreadcrumbItem } from '@/services/breadcrumbs';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { generatePath } from '@/services/router/generate-path';
import { appendMasqParam } from '@/utils/masq';

function AuditLogsPageLayout({
  children,
  breadcrumbs,
  pageTitle,
}: {
  children: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  pageTitle: string;
}) {
  const is2026LayoutRefresh = useFeatureFlag(FeatureFlag.LayoutRefresh2026);

  useRegisterBreadcrumbs(breadcrumbs ?? [], 'root');

  return (
    <>
      <PageTitle title={pageTitle} />
      <Page style={{ width: '100%' }}>
        {!is2026LayoutRefresh ? (
          <PageBreadcrumbs>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </PageBreadcrumbs>
        ) : null}
        {children}
      </Page>
    </>
  );
}

interface AuditLogsPageContainerProps {
  context: 'account' | 'workspace';
  contextId: number | null | undefined;
  /** Workspace ID for exports link. For workspace context, this should match contextId */
  workspaceId: number | null;
  breadcrumbs: BreadcrumbItem[];
  pageTitle: string;
  pageDescription: React.ReactNode;
  backUrlBase: string;
  feedbackFeature: string;
}

export const AuditLogsPageContainer: React.FC<AuditLogsPageContainerProps> = ({
  context,
  contextId,
  workspaceId,
  breadcrumbs,
  pageTitle,
  pageDescription,
  backUrlBase,
  feedbackFeature,
}) => {
  const { track } = useAnalytics();
  const navigate = useSafeNavigate();
  const showFeedbackModal = useCustomerFeedback();
  const exportModal = useExportAuditLogsModal();

  const {
    filters,
    uiObjectType,
    auditLogId,
    accumulatedLogs,
    auditLogsQuery,
    singleLogQuery,
    allUsersQuery,
    objectTypesQuery,
    workspaces,
    handleFiltersChange,
    handleSetUiObjectType,
    handleLoadUsers,
    segments,
    isLoadingSegments,
    // Campaign props
    campaigns,
    isLoadingCampaigns,

    // Newsletter props
    newsletters,
    isLoadingNewsletters,
    // Transactional message props
    transactionalMessages,
    isLoadingTransactionalMessages,
    // Legacy props
    isLoadingUsers,
    messages,
    isLoadingMessages,
    roleDefinitions,
  } = useAuditLogsData({
    context,
    contextId,
  });

  // Track page view (filters computed inside to avoid duplicate tracking on filter changes)
  const trackPageView = useCallback(() => {
    // Check if any filters are applied (excluding pagination/sorting)
    const hasFilters = !!(
      filters.user_id != null ||
      !isEmptyString(filters.ip_addr) ||
      !isEmptyString(filters.object_type) ||
      !isEmptyString(filters.object_id) ||
      !isEmptyString(filters.attribute_name) ||
      !isEmptyString(filters.action) ||
      !isEmptyString(filters.start_time) ||
      !isEmptyString(filters.end_time)
    );

    track('auditLogs', 'viewed', {
      contextType: context,
      workspaceId: contextId,
      hasFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prevent duplicate tracking
  }, [context, contextId, track]);

  usePageViewTracking(trackPageView, contextId !== null);

  // Eagerly load users on mount to avoid popover closing issue
  useEffect(() => {
    handleLoadUsers();
  }, [handleLoadUsers]);

  // Handle deep link to specific audit log
  if (auditLogId != null) {
    if (singleLogQuery.isLoading) {
      return (
        <AuditLogsPageLayout pageTitle={pageTitle} breadcrumbs={breadcrumbs}>
          <PageHeader>
            <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
            <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
          </PageHeader>
          <PageBody>
            <Box p="800" textAlign="center">
              <Spinner />
              <Text mt="300">Loading audit log...</Text>
            </Box>
          </PageBody>
        </AuditLogsPageLayout>
      );
    }

    if (singleLogQuery.error != null || singleLogQuery.data == null) {
      const auditLogHref =
        context === 'account'
          ? appendMasqParam(`/settings/audit-logs?audit_log_id=${auditLogId}`)
          : generatePath(
              '/workspaces/:workspaceId/settings/audit-logs',
              { workspaceId: contextId ?? 'last' },
              { audit_log_id: auditLogId },
            );

      const singleLogBreadcrumbs = [
        ...breadcrumbs,
        {
          id: 'audit_logs.audit-log',
          text: `Audit Log #${auditLogId}`,
          href: auditLogHref,
        },
      ];
      const errorMessage =
        singleLogQuery.error != null
          ? `Error loading audit log: ${
              singleLogQuery.error instanceof Error ? singleLogQuery.error.message : 'Unknown error'
            }`
          : `Audit log #${auditLogId} not found`;
      const errorDetails =
        'This audit log may not exist, may have been deleted, or you may not have permission to view it.';

      return (
        <AuditLogsPageLayout pageTitle={pageTitle} breadcrumbs={singleLogBreadcrumbs}>
          <PageHeader>
            <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
            <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
          </PageHeader>
          <PageBody>
            <Banner variant="error" title={errorMessage}>
              <Text>{errorDetails}</Text>
            </Banner>
          </PageBody>
        </AuditLogsPageLayout>
      );
    }

    // Create a response object compatible with AuditLogsTable
    const deepLinkedResponse: AuditLogResponse = {
      data: [singleLogQuery.data.data],
      users: singleLogQuery.data.user != null ? [singleLogQuery.data.user] : [],
      service_accounts: singleLogQuery.data.service_account != null ? [singleLogQuery.data.service_account] : undefined,
      pagination: {
        limit: 1,
        sort_order: 'desc',
        next_cursor: 0,
      },
    };

    const auditLogHref =
      context === 'account'
        ? appendMasqParam(`/settings/audit-logs?audit_log_id=${auditLogId}`)
        : generatePath(
            '/workspaces/:workspaceId/settings/audit-logs',
            { workspaceId: contextId ?? 'last' },
            { audit_log_id: auditLogId },
          );
    const singleLogBreadcrumbs = [
      ...breadcrumbs,
      {
        id: 'audit_logs.audit-log',
        text: `Audit Log #${auditLogId}`,
        href: auditLogHref,
      },
    ];

    return (
      <AuditLogsPageLayout pageTitle={pageTitle} breadcrumbs={singleLogBreadcrumbs}>
        <PageHeader>
          <PageHeaderTitle>Audit Log #{auditLogId}</PageHeaderTitle>
          <PageHeaderDescription>Viewing a single audit log entry.</PageHeaderDescription>
        </PageHeader>
        <PageBody>
          <Stack gap="400">
            <Box>
              <Link
                icon="chevron-left"
                variant="secondary"
                onClick={(event) => {
                  event.preventDefault();
                  navigate(backUrlBase);
                }}
              >
                Back to All Logs
              </Link>
            </Box>
            <AuditLogsTable
              data={deepLinkedResponse}
              isLoading={false}
              error={undefined}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onSetUiObjectType={handleSetUiObjectType}
              uiObjectType={uiObjectType}
              highlightedLogId={auditLogId.toString()}
              context={context}
              contextId={contextId ?? 0}
              allUsers={allUsersQuery.data?.users ?? []}
              isLoadingUsers={isLoadingUsers}
              onLoadUsers={handleLoadUsers}
              campaigns={campaigns}
              isLoadingCampaigns={isLoadingCampaigns}
              segments={segments}
              isLoadingSegments={isLoadingSegments}
              newsletters={newsletters}
              isLoadingNewsletters={isLoadingNewsletters}
              transactionalMessages={transactionalMessages}
              isLoadingTransactionalMessages={isLoadingTransactionalMessages}
              workspaces={workspaces}
              roleDefinitions={roleDefinitions}
              objectTypes={objectTypesQuery.data ?? []}
              messages={messages}
              isLoadingMessages={isLoadingMessages}
            />
          </Stack>
        </PageBody>
      </AuditLogsPageLayout>
    );
  }

  // Get current object type filter for export
  const currentObjectType = filters.object_type;

  // Normal audit logs list view
  return (
    <AuditLogsPageLayout pageTitle={pageTitle} breadcrumbs={breadcrumbs}>
      <PageHeader>
        <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
        <PageHeaderActions>
          <InlineStack gap="100">
            <Button
              variant="secondary"
              icon="export"
              onClick={() =>
                exportModal.open({ context, contextId: contextId ?? null, workspaceId, workspaces, currentObjectType })
              }
            >
              Export CSV
            </Button>
            <Button
              variant="tertiary"
              icon="slack"
              onClick={() => {
                void showFeedbackModal({ feature: feedbackFeature });
              }}
            >
              Feedback
            </Button>
          </InlineStack>
        </PageHeaderActions>
        <PageHeaderDescription>{pageDescription}</PageHeaderDescription>
      </PageHeader>
      <PageBody>
        {/* Audit Logs Table */}
        <AuditLogsTable
          data={accumulatedLogs}
          isLoading={auditLogsQuery?.isLoading && filters.cursor == null}
          error={auditLogsQuery?.error}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSetUiObjectType={handleSetUiObjectType}
          uiObjectType={uiObjectType}
          highlightedLogId={auditLogId != null ? String(auditLogId) : undefined}
          context={context}
          contextId={contextId ?? 0}
          allUsers={allUsersQuery.data?.users ?? []}
          isLoadingUsers={isLoadingUsers}
          onLoadUsers={handleLoadUsers}
          campaigns={campaigns}
          isLoadingCampaigns={isLoadingCampaigns}
          segments={segments}
          isLoadingSegments={isLoadingSegments}
          newsletters={newsletters}
          isLoadingNewsletters={isLoadingNewsletters}
          transactionalMessages={transactionalMessages}
          isLoadingTransactionalMessages={isLoadingTransactionalMessages}
          workspaces={workspaces}
          roleDefinitions={roleDefinitions}
          objectTypes={objectTypesQuery.data ?? []}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
        />
      </PageBody>
    </AuditLogsPageLayout>
  );
};
